/**
 * Transform Preview Component
 * 
 * Renders preview of entities during transform operations
 * (move, copy, rotate, scale, mirror).
 */

import React, { useMemo } from 'react';
import { Line } from '@react-three/drei';
import * as THREE from 'three';
import { useShallow } from 'zustand/react/shallow';
import { useTransformStore } from './EditTools';
import { useAppStore, Entity } from '../../state/store';

interface TransformPreviewProps {
    selectedIds: string[];
}

/**
 * Transform Preview - Shows ghost preview of transformed entities
 */
export const TransformPreview: React.FC<TransformPreviewProps> = ({ selectedIds }) => {
    // Narrowed selector (was a whole-store destructure): the preview must not
    // re-render on unrelated transform-store writes (previewMatrix, bounds...).
    const { mode, isActive, basePoint, currentPoint } = useTransformStore(
        useShallow((s) => ({
            mode: s.mode,
            isActive: s.isActive,
            basePoint: s.basePoint,
            currentPoint: s.currentPoint,
        }))
    );
    const entities = useAppStore((s) => s.entities);

    // Calculate delta/transformation
    const delta = useMemo(() => {
        if (!currentPoint || !basePoint) return new THREE.Vector3();
        return new THREE.Vector3().subVectors(currentPoint, basePoint);
    }, [basePoint, currentPoint]);

    // Don't render if not transforming
    if (!isActive || !basePoint || !currentPoint || mode === 'none') {
        return null;
    }

    return (
        <group>
            {/* Reference line from base point to current point */}
            <Line
                points={[basePoint, currentPoint]}
                color="#ffff00"
                lineWidth={1}
                dashed
                dashSize={0.2}
                gapSize={0.1}
            />

            {/* Base point marker */}
            <mesh position={basePoint}>
                <sphereGeometry args={[0.08, 8, 8]} />
                <meshBasicMaterial color="#00ff00" />
            </mesh>

            {/* Current point marker */}
            <mesh position={currentPoint}>
                <sphereGeometry args={[0.08, 8, 8]} />
                <meshBasicMaterial color="#ff0000" />
            </mesh>

            {/* Preview for each selected entity */}
            {selectedIds.map((id) => {
                const entity = entities[id];
                if (!entity) return null;

                return (
                    <EntityPreview
                        key={`preview-${id}`}
                        entity={entity}
                        delta={delta}
                    />
                );
            })}
        </group>
    );
};

/**
 * Entity Preview - Preview for individual entity based on type
 */
const EntityPreview: React.FC<{
    entity: Entity;
    delta: THREE.Vector3;
}> = ({ entity, delta }) => {
    const previewColor = '#00aaff';
    const opacity = 0.6;

    switch (entity.type) {
        case 'line':
            return (
                <LinePreview
                    data={entity.data}
                    delta={delta}
                    color={previewColor}
                />
            );

        case 'wall':
            return (
                <WallPreview
                    data={entity.data}
                    delta={delta}
                    color={previewColor}
                    opacity={opacity}
                />
            );

        case 'circle':
            return (
                <CirclePreview
                    data={entity.data}
                    delta={delta}
                    color={previewColor}
                />
            );

        case 'rectangle':
            return (
                <RectanglePreview
                    data={entity.data}
                    delta={delta}
                    color={previewColor}
                />
            );

        case 'box':
            return (
                <BoxPreview
                    data={entity.data}
                    delta={delta}
                    color={previewColor}
                    opacity={opacity}
                />
            );

        default:
            return null;
    }
};

/**
 * Line Preview
 */
const LinePreview: React.FC<{
    data: Record<string, unknown>;
    delta: THREE.Vector3;
    color: string;
}> = ({ data, delta, color }) => {
    const start = useMemo(() => {
        const s = data.start as number[];
        return new THREE.Vector3(s[0] + delta.x, s[1] + delta.y, s[2] + delta.z);
    }, [data.start, delta]);

    const end = useMemo(() => {
        const e = data.end as number[];
        return new THREE.Vector3(e[0] + delta.x, e[1] + delta.y, e[2] + delta.z);
    }, [data.end, delta]);

    return <Line points={[start, end]} color={color} lineWidth={2} />;
};

/**
 * Wall Preview
 */
const WallPreview: React.FC<{
    data: Record<string, unknown>;
    delta: THREE.Vector3;
    color: string;
    opacity: number;
}> = ({ data, delta, color, opacity }) => {
    const { position, rotation, size } = useMemo(() => {
        const center = data.center as number[];
        const length = data.length as number;
        const height = data.height as number;
        const thickness = data.thickness as number;
        const angle = data.angle as number;

        return {
            position: new THREE.Vector3(
                center[0] + delta.x,
                center[1] + delta.y,
                center[2] + delta.z
            ),
            rotation: [0, -angle, 0] as [number, number, number],
            size: [length, height, thickness] as [number, number, number],
        };
    }, [data, delta]);

    return (
        <mesh position={position} rotation={rotation}>
            <boxGeometry args={size} />
            <meshStandardMaterial
                color={color}
                transparent
                opacity={opacity}
                wireframe
            />
        </mesh>
    );
};

/**
 * Circle Preview
 */
const CirclePreview: React.FC<{
    data: Record<string, unknown>;
    delta: THREE.Vector3;
    color: string;
}> = ({ data, delta, color }) => {
    const points = useMemo(() => {
        const center = data.center as number[];
        const radius = data.radius as number;
        const pts: THREE.Vector3[] = [];
        const segments = 32;

        for (let i = 0; i <= segments; i++) {
            const angle = (i / segments) * Math.PI * 2;
            pts.push(new THREE.Vector3(
                center[0] + delta.x + Math.cos(angle) * radius,
                center[1] + delta.y,
                center[2] + delta.z + Math.sin(angle) * radius
            ));
        }

        return pts;
    }, [data, delta]);

    return <Line points={points} color={color} lineWidth={2} />;
};

/**
 * Rectangle Preview
 */
const RectanglePreview: React.FC<{
    data: Record<string, unknown>;
    delta: THREE.Vector3;
    color: string;
}> = ({ data, delta, color }) => {
    const points = useMemo(() => {
        const c1 = data.corner1 as number[];
        const c2 = data.corner2 as number[];

        return [
            new THREE.Vector3(c1[0] + delta.x, c1[1] + delta.y, c1[2] + delta.z),
            new THREE.Vector3(c2[0] + delta.x, c1[1] + delta.y, c1[2] + delta.z),
            new THREE.Vector3(c2[0] + delta.x, c1[1] + delta.y, c2[2] + delta.z),
            new THREE.Vector3(c1[0] + delta.x, c1[1] + delta.y, c2[2] + delta.z),
            new THREE.Vector3(c1[0] + delta.x, c1[1] + delta.y, c1[2] + delta.z),
        ];
    }, [data, delta]);

    return <Line points={points} color={color} lineWidth={2} />;
};

/**
 * Box Preview
 */
const BoxPreview: React.FC<{
    data: Record<string, unknown>;
    delta: THREE.Vector3;
    color: string;
    opacity: number;
}> = ({ data, delta, color, opacity }) => {
    const { position, size } = useMemo(() => {
        const pos = data.position as number[] || [0, 0, 0];
        const sz = data.size as number[] || [1, 1, 1];

        return {
            position: new THREE.Vector3(
                pos[0] + delta.x,
                pos[1] + delta.y,
                pos[2] + delta.z
            ),
            size: sz as [number, number, number],
        };
    }, [data, delta]);

    return (
        <mesh position={position}>
            <boxGeometry args={size} />
            <meshStandardMaterial
                color={color}
                transparent
                opacity={opacity}
                wireframe
            />
        </mesh>
    );
};

export default TransformPreview;
